import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/calculator.css";

import { PriceRequest } from "../models/PriceRequest";
import { CalculatorService } from "../services/CalculatorService";
import { BoxesForm } from "../components/forms/BoxesForm";
import { BoxInput } from "../types/form";
import { BASE_URLS } from "../config/urls";
import {BtnSpinner} from "../components/fields/BtnSpinner"; // <-- проверь путь

type Box = {
    length: number;
    width: number;
    height: number;
    amount: number;
};

const service = new CalculatorService();

const STORE_OPTIONS = ["Wildberries", "Ozon", "Яндекс Маркет"];

const Calculator: React.FC = () => {
    const [store, setStore] = useState("Wildberries");

    // города теперь с бэка
    const [cities, setCities] = useState<string[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [citiesError, setCitiesError] = useState<string>("");

    const [sendCity, setSendCity] = useState(""); // <-- будет выставляться после загрузки cities

    const [inputs, setInputs] = useState<BoxInput[]>([
        { length: 0, width: 0, height: 0, amount: 0 },
    ]);

    const [price, setPrice] = useState<number>(0);
    const [volume, setVolume] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const resultRef = useRef<HTMLDivElement | null>(null);

    const { totalVolumeM3, totalAmount, isValid } = useMemo(() => {
        let v = 0;
        let amount = 0;
        let valid = true;

        for (const it of inputs) {
            const L = Number(it.length) || 0;
            const W = Number(it.width) || 0;
            const H = Number(it.height) || 0;
            const A = Number(it.amount) || 0;

            if (L <= 0 || W <= 0 || H <= 0 || A <= 0) valid = false;

            v += L * W * H * A; // см^3 * qty
            amount += A;
        }

        return { totalVolumeM3: v / 1_000_000, totalAmount: amount, isValid: valid && v > 0 };
    }, [inputs]);

    // 1) загрузка городов при смене склада
    useEffect(() => {
        let cancelled = false;

        const fetchCities = async () => {
            setCitiesLoading(true);
            setCitiesError("");

            try {
                const resp = await fetch(
                    `${BASE_URLS.api}/price_request/cities?store=${encodeURIComponent(store)}`,
                    { headers: { Accept: "application/json" } }
                );

                if (!resp.ok) {
                    throw new Error(`Не удалось загрузить города (HTTP ${resp.status})`);
                }

                // ожидаем массив строк: ["Казань", "Москва", ...]
                const data = (await resp.json()) as string[];

                if (cancelled) return;

                const list = Array.isArray(data) ? data : [];
                setCities(list);

                // если текущий выбранный город не подходит — ставим первый
                setSendCity((prev) => {
                    if (prev && list.includes(prev)) return prev;
                    return list[0] ?? "";
                });
            } catch (e: any) {
                if (cancelled) return;
                setCities([]);
                setSendCity("");
                setCitiesError(e?.message || "Ошибка при загрузке городов");
            } finally {
                if (!cancelled) setCitiesLoading(false);
            }
        };

        fetchCities();
        return () => {
            cancelled = true;
        };
    }, [store]);

    async function countPrice() {
        setError("");
        setPrice(0);
        setLoading(true);

        try {
            if (!isValid || !sendCity) {
                setPrice(0);
                return;
            }

            setVolume(totalVolumeM3);

            const req = new PriceRequest(store, sendCity, totalVolumeM3, false, false, totalAmount);

            const boxes: Box[] = inputs.map((i) => ({
                length: Number(i.length) || 0,
                width: Number(i.width) || 0,
                height: Number(i.height) || 0,
                amount: Number(i.amount) || 0,
            }));

            const { price: p, volume: volFromServer } = await service.calculate(req, boxes);

            const finalVol =
                Number.isFinite(volFromServer) && volFromServer > 0 ? volFromServer : totalVolumeM3;

            setVolume(finalVol);
            setPrice(p || 0);
        } catch (e: any) {
            console.error(e);
            setError(e?.message || "Неизвестная ошибка при расчёте.");
            setPrice(0);
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
        }
    }

    const canCalculate = !loading && isValid && !!sendCity && !citiesLoading && !citiesError;

    return (
        <div className="calculator-page">
            <div className="calculator-hero">
                <div className="calculator-hero-card">
                    <div className="calculator-badge">Расчёт стоимости</div>
                    <h1 className="calculator-title">Калькулятор поставки</h1>
                    <p className="calculator-subtitle">
                        Выберите склад и город назначения, затем добавьте коробки — посчитаем объём и цену.
                    </p>

                    <div className="calculator-controls">
                        <div className="calculator-field">
                            <label className="calculator-label">Склад</label>
                            <select
                                className="calculator-select"
                                value={store}
                                onChange={(e) => setStore(e.target.value)}
                            >
                                {STORE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="calculator-field">
                            <label className="calculator-label">Город назначения</label>

                            <select
                                className="calculator-select"
                                value={sendCity}
                                onChange={(e) => setSendCity(e.target.value)}
                                disabled={citiesLoading || !!citiesError || cities.length === 0}
                            >
                                {citiesLoading && <option value="">Загрузка...</option>}
                                {!citiesLoading && cities.length === 0 && <option value="">Нет городов</option>}

                                {!citiesLoading &&
                                    cities.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                            </select>

                            {citiesError && <div className="calculator-error">{citiesError}</div>}
                        </div>
                    </div>

                    <BoxesForm inputs={inputs} onChange={setInputs} />

                    <div className="calculator-actions">
                        <button
                            type="button"
                            className="calculator-primary-btn"
                            onClick={countPrice}
                            disabled={!canCalculate}
                        >
                            {loading ? <BtnSpinner/> : "Посчитать"}
                        </button>

                        <div className="calculator-note">Объём считается в м³ по введённым размерам (см).</div>
                    </div>

                    <div ref={resultRef} className="calculator-result">
                        <div className="calculator-result-card">
                            <div className="calculator-result-title">Результат</div>

                            {error && <div className="calculator-error">{error}</div>}

                            <div className="calculator-result-grid">
                                <div className="calculator-result-item">
                                    <div className="calculator-result-label">Объём</div>
                                    <div className="calculator-result-value">
                                        {volume > 0 ? `${volume.toFixed(4)} м³` : "—"}
                                    </div>
                                </div>

                                <div className="calculator-result-item">
                                    <div className="calculator-result-label">Стоимость</div>
                                    <div className="calculator-result-value calculator-result-value--price">
                                        {price || "—"}
                                    </div>
                                </div>
                            </div>

                            <div className="calculator-result-hint">
                                Если цена “—”, проверьте введённые данные или попробуйте ещё раз.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="calculator-side">
                    <div className="calculator-side-card">
                        <div className="calculator-side-title">Подсказки</div>
                        <ul className="calculator-side-list">
                            <li>Размеры вводите в сантиметрах.</li>
                            <li>Кол-во — число коробок такого размера.</li>
                            <li>Можно добавлять несколько строк коробок.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Calculator;
